import {AssignType} from "../list-item";
import {EcgWorkflow} from "../../hospitals/edit-hospital/hospital.details";
import {Doctor} from "../doctor";
import {Chain} from "../found-measurement";
import {isEcgMeasurement, MeasurementType} from "../../measurement-models";
import {ReleaseToggleState} from "../../../tools/release-toggles/release-toggle-state";
import {AnalyzerOption} from "./doctor-selector.component";

export interface CreateAssignOptionsAndSelectInitialAnalyzerParams {
  assignType: AssignType | undefined;
  doctor: Doctor | undefined;
  chain: Chain | undefined;
  ecgWorkFlow: EcgWorkflow | undefined;
  measurementType: MeasurementType;
  doctors: Doctor[];
  selectingTechnicians: boolean;
  globalTechnicianPoolLabel: string;
}

export function createAssignOptionsAndSelectInitialAnalyzer(params: CreateAssignOptionsAndSelectInitialAnalyzerParams): {
  selectedAnalyzerIndex: number | undefined;
  options: AnalyzerOption[];
} {
  const options: AnalyzerOption[] = buildDoctorOptions(params.doctors);

  if (shouldShowChain(params.chain, params.selectingTechnicians)) {
    options.unshift(createChainOption(params.chain!));
  }

  if (shouldShowGlobalTechnicianPool(params)) {
    options.unshift(createGlobalTechnicianPoolOption(params.globalTechnicianPoolLabel));
  }

  return {
    selectedAnalyzerIndex: selectInitialIndex(options, params.assignType, params.doctor),
    options
  };
}

function buildDoctorOptions(doctors: Doctor[]): AnalyzerOption[] {
  return doctors.map((doctor): AnalyzerOption => ({
    doctorId: doctor.id,
    assignType: "DOCTOR",
    text: `${doctor.firstName} ${doctor.lastName}`,
    testId: `doctor-select-${doctor.firstName}-${doctor.lastName}`
  }));
}

function createChainOption(chain: Chain): AnalyzerOption {
  return {
    doctorId: undefined,
    assignType: "CHAIN",
    text: chain.name!,
    testId: "doctor-select-chain"
  };
}

function createGlobalTechnicianPoolOption(label: string): AnalyzerOption {
  return {
    doctorId: undefined,
    assignType: "GLOBAL_TECHNICIAN_POOL",
    text: label,
    testId: "doctor-select-global-technician-pool"
  };
}

function shouldShowChain(
  chain: Chain | undefined,
  selectingTechnicians: boolean
): boolean {
  return chain !== undefined && !selectingTechnicians;
}

function shouldShowGlobalTechnicianPool(params: CreateAssignOptionsAndSelectInitialAnalyzerParams): boolean {
  if (!isEcgMeasurement(params.measurementType)) {
    return false;
  }

  const technicianAnalysisOnly =
    !params.selectingTechnicians &&
    params.ecgWorkFlow === "TECHNICIAN_ANALYSIS_ONLY" &&
    ReleaseToggleState.getInstance().isReleased("ASSIGN_GLOBAL_TECHNICIAN_POOL_AS_DOCTOR_485");

  const technicianPreAnalysis =
    params.selectingTechnicians &&
    params.ecgWorkFlow === "TECHNICIAN_PRE_ANALYSIS" &&
    ReleaseToggleState.getInstance().isReleased("ASSIGN_GLOBAL_TECHNICIAN_POOL_FOR_PREANALYSIS_484");

  return technicianAnalysisOnly || technicianPreAnalysis;
}

function selectInitialIndex(
  options: AnalyzerOption[],
  assignType: AssignType | undefined,
  doctor: Doctor | undefined
): number | undefined {
  if (assignType === "DOCTOR" && doctor) {
    return options.findIndex(
      (option) => option.assignType === "DOCTOR" && option.doctorId === doctor.id
    );
  }

  const globalPoolIndex = options.findIndex((o) => o.assignType === "GLOBAL_TECHNICIAN_POOL");
  if (globalPoolIndex !== -1) {
    return globalPoolIndex;
  }

  const chainIndex = options.findIndex((o) => o.assignType === "CHAIN");
  if (chainIndex !== -1) {
    return chainIndex;
  }

  return undefined;
}
